import React, { forwardRef, ReactNode, Ref } from "react"
import { ClickAwayListener, Popover, PopoverActions, PopoverOrigin } from "@mui/material"
import styled from "styled-components"
import { theme } from "../../../theme"
import { Button, ButtonArea } from "../../atoms/button"
import { TextBold, TextRegular } from "../../atoms/text"
import { Close, DeleteOutline } from "@mui/icons-material"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
export type PopperVerticalPosition = "top" | "center" | "bottom"

export type PopperHorizontalPosition = "left" | "center" | "right"

export type PopperOrigin = {
  vertical?: PopperVerticalPosition
  horizontal?: PopperHorizontalPosition
}

export type PopperProps = {
  title?: string
  onClose: () => void
  onDelete?: () => void
  children?: ReactNode
  className?: string
  anchorEl: HTMLElement | null
  anchorOrigin?: PopperOrigin
  transformOrigin?: PopperOrigin
  popperAddNew?: PopperAddNew
}

export type PopperAddNew = {
  addNewText: string
  onBack: () => void
  onAdd: () => void
}

export const Popper = forwardRef((props: PopperProps, ref) => {
  const { title, children, className, onClose, onDelete, anchorEl, popperAddNew } = props

  const anchorOrigin: PopoverOrigin = {
    vertical: props.anchorOrigin?.vertical ?? "bottom",
    horizontal: props.anchorOrigin?.horizontal ?? "left",
  }

  const transformOrigin: PopoverOrigin = {
    vertical: props.transformOrigin?.vertical ?? "top",
    horizontal: props.transformOrigin?.horizontal ?? "left",
  }

  const handleDelete = () => {
    onDelete && onDelete()
    onClose()
  }

  const open = !!anchorEl

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
      action={ref as Ref<PopoverActions>}
    >
      <ClickAwayListener onClickAway={onClose}>
        <PopperMenu className={className}>
          {!popperAddNew && (
            <>
              {title && (
                <PopperMenuHeader>
                  <PopperMenuTitle>
                    <TextRegular>{title}</TextRegular>
                  </PopperMenuTitle>
                  <PopperMenuActions>
                    {onDelete && <PopperMenuAction onClick={handleDelete} icon={<DeleteOutline />} />}
                    <PopperMenuAction onClick={onClose} icon={<Close />} data-cy="closePopover" />
                  </PopperMenuActions>
                </PopperMenuHeader>
              )}

              <PopperMenuContent>{children}</PopperMenuContent>
            </>
          )}
          {popperAddNew && (
            <>
              <PopperAddNewMenuHeader>
                <PopperGoBackAction onClick={popperAddNew.onBack} icon={<ArrowBackIcon />} data-cy="popperGoBack" />
                <PopperMenuTitle>
                  <PopperAddNewText>{popperAddNew.addNewText}</PopperAddNewText>
                </PopperMenuTitle>
              </PopperAddNewMenuHeader>
              <PopperMenuContent>{children}</PopperMenuContent>
              <AddButton onClick={() => popperAddNew.onAdd()}>Add</AddButton>
            </>
          )}
        </PopperMenu>
      </ClickAwayListener>
    </Popover>
  )
})

const PopperMenu = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 80vw;
  min-width: 350px;
  min-height: 100px;
  max-height: 450px;
  overflow-y: auto;
  color: ${(props) => props.theme.color};
  background: ${(props) => props.theme.background};
  padding: ${theme.spacing.lx};
  border-radius: ${theme.borderRadius};
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
  z-index: ${theme.zIndex.high};
  gap: ${theme.spacing.l};
`

const PopperMenuHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const PopperMenuTitle = styled.span`
  color: ${theme.color.grey2};
`

const PopperMenuActions = styled.div`
  display: flex;
  gap: ${theme.spacing.m};
`

const PopperMenuAction = styled(ButtonArea)`
  padding: ${theme.spacing.s};
  border-radius: ${theme.borderRadius};
  color: ${(props) => props.theme.primaryColor};
  display: flex;
  justify-content: center;
  align-items: center;

  svg {
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
  }
`

const PopperAddNewMenuHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.m};
`

const PopperGoBackAction = styled(ButtonArea)`
  border-radius: ${theme.borderRadius};
  color: ${(props) => props.theme.primaryColor};
  display: flex;
  justify-content: start;
  align-items: center;
  width: ${theme.iconSize.l};
  height: ${theme.iconSize.l};

  span {
    align-items: center;
  }

  svg {
    width: ${theme.iconSize.l};
    height: ${theme.iconSize.l};
  }
`

const PopperAddNewText = styled(TextBold)`
  color: ${(props) => props.theme.primaryColor};
`

const PopperMenuContent = styled.div`
  padding-top: 0;
`

const AddButton = styled(Button)`
  color: ${(props) => props.theme.primaryColor};
  border-color: ${(props) => props.theme.primaryColor};
  align-self: end;

  width: fit-content;

  :hover {
    border-color: ${(props) => props.theme.primaryColor};
  }
`
